<template>
  <div class="tabs-view-container">
    <div class="tabs-view-header">
      <div class="tabs-main">
        <el-tabs 
          v-model="activeTab" 
          type="card" 
          closable 
          @tab-click="handleTabClick"
          @tab-remove="handleTabRemove">
          <el-tab-pane
            v-for="tab in visitedTabs"
            :key="tab.path"
            :label="tab.title"
            :name="tab.path">
            <!-- 标签内容为空，实际内容由router-view显示 -->
          </el-tab-pane>
        </el-tabs>
        
        <!-- 标签操作按钮，独立于标签栏 -->
        <div class="tabs-actions-wrapper" v-if="visitedTabs.length > 0">
          <el-dropdown @command="handleTabsAction" trigger="click" placement="bottom-end">
            <el-button type="text" size="mini" class="tabs-action-button">
              <i class="el-icon-more"></i>
            </el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="closeRight">关闭右侧</el-dropdown-item>
              <el-dropdown-item command="closeOthers">关闭其他</el-dropdown-item>
              <el-dropdown-item command="closeAll">关闭全部</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TabsView',
  data() {
    return {
      activeTab: '',
      visitedTabs: [],
      cachedTabs: []
    }
  },
  watch: {
    $route(route) {
      this.addVisitedTab(route)
    }
  },
  mounted() {
    // 从localStorage恢复已访问标签
    this.restoreTabs()
    // 添加当前路由对应的标签
    this.addVisitedTab(this.$route)
    // 监听全局关闭标签事件（如保存成功后关闭编辑页标签）
    this.$root.$on('tabs:close', this.closeTabByPath)
  },
  beforeDestroy() {
    this.$root.$off('tabs:close', this.closeTabByPath)
  },
  methods: {
    restoreTabs() {
      // 从localStorage恢复已访问标签
      const storedTabs = localStorage.getItem('visitedTabs')
      if (storedTabs) {
        try {
          const parsedTabs = JSON.parse(storedTabs)
          // 最多恢复10个标签，避免过多
          // 不恢复动态详情标签（如 /noticeDetail/5）：旧的 id 可能已失效，
          // 否则点击会用过期/缺失的 id 进详情页，触发“通知不存在或已失效”
          this.visitedTabs = parsedTabs
            .slice(0, 10)
            .filter(tab => !(tab.path && tab.path.startsWith('/noticeDetail')))
        } catch (e) {
          console.error('解析存储的标签失败:', e)
          this.visitedTabs = []
        }
      }
    },
    saveTabs() {
      // 保存标签到localStorage
      localStorage.setItem('visitedTabs', JSON.stringify(this.visitedTabs))
    },
    isTabExist(path) {
      // 处理带参数的路由，如 /storeedit/device 应该与 /storeedit/123 视为相同类型
      const currentBasePath = this.getBaseRoutePath(path);
      return this.visitedTabs.some(tab => {
        const tabBasePath = this.getBaseRoutePath(tab.path);
        return tabBasePath === currentBasePath;
      });
    },
    
    // 获取路由的基本路径（不包含参数部分）
    getBaseRoutePath(path) {
      // 处理带参数的路由
      if (path.includes('/storeedit/')) {
        return '/storeedit';
      } else if (path.includes('/device/')) {
        return '/device';
      } else if (path.includes('/noticeDetail/')) {
        // 通知详情是 /noticeDetail/:id 动态路由，不登记基础路径会被下方
        // “动态路由不添加标签”规则跳过，导致标签栏与实际页面错位、
        // 残留旧标签点击后用过期 id 重新请求而报“加载通知详情失败”
        return '/noticeDetail';
      }
      // 可以根据需要添加更多的路由匹配规则
      
      return path;
    },
    getTabTitle(route) {
      // 尝试从路由的meta中获取标题
      if (route.meta && route.meta.title) {
        return route.meta.title
      }
      
      // 尝试从菜单中获取标题
      const menuList = JSON.parse(localStorage.getItem('menuList') || '[]')
      const findMenuTitle = (list) => {
        for (const menu of list) {
          if (menu.router === route.path) {
            return menu.lbname
          }
          if (menu.children && menu.children.length > 0) {
            const title = findMenuTitle(menu.children)
            if (title) return title
          }
        }
        return null
      }
      
      const menuTitle = findMenuTitle(menuList)
      if (menuTitle) return menuTitle
      
      // 如果都没有，使用路径最后一段作为标题
      const pathSegments = route.path.split('/')
      const lastSegment = pathSegments[pathSegments.length - 1]
      return lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1)
    },
    addVisitedTab(route) {
      const path = route.path

      // 增加调试日志
      console.log('尝试添加标签页:', { path, route });

      // 如果是登录页或首页，不添加标签
      if (path === '/login' || path === '/') {
        console.log('登录页或首页，不添加标签');
        return;
      }

      // 特别处理：不在标签页中展示和存储动态路由（如/customer-store-info/3）
      // 检查是否是动态路由模式（如/xxx/123形式）
      // 通知详情(/noticeDetail/:id)除外：它是正常业务页，需要登记标签，
      // 且已在 getBaseRoutePath 中按基础路径去重
      if (path.match(/\/[\w-]+\/\d+$/) && !path.includes('/noticeDetail/')) {
        console.log('检测到动态路由，不添加标签:', path);
        return;
      }

      // 特别处理customer-store-info路由
      if (path.includes('/customer-store-info/')) {
        console.log('检测到customer-store-info路由，不添加标签:', path);
        return;
      }

      // 更全面地过滤API路径，避免将API路径添加为标签
      // 1. 检查路径是否包含API路径关键字
      // 2. 检查sessionStorage中的API请求标记
      // 3. 检查当前URL是否包含API路径
      if (
        path.includes('/stores/full_info') || 
        path.includes('/api/') || 
        window.sessionStorage.getItem('isApiRequest') === 'true' ||
        window.location.href.includes('/stores/full_info')
      ) {
        console.log('检测到API路径，不添加标签:', path);
        return;
      }

      // 确保路由有匹配的组件，避免添加无效路由
      if (!route.matched || route.matched.length === 0) {
        console.log('路由无匹配组件，不添加标签:', path);
        return;
      }

      // 获取基本路径用于去重
      const baseRoutePath = this.getBaseRoutePath(path);

      // 如果标签已存在，只激活它并更新路径
      const existingTabIndex = this.visitedTabs.findIndex(tab => 
        this.getBaseRoutePath(tab.path) === baseRoutePath
      );

      if (existingTabIndex !== -1) {
        console.log('标签已存在，更新路径:', { 
          oldPath: this.visitedTabs[existingTabIndex].path, 
          newPath: path 
        });

        // 更新标签的路径为当前路径
        this.visitedTabs[existingTabIndex].path = path;
        // 更新访问时间
        this.visitedTabs[existingTabIndex].time = new Date().getTime();
        this.activeTab = path;
        this.saveTabs();
        return;
      }

      // 获取标签标题
      const title = this.getTabTitle(route);
      console.log('添加新标签:', { path, title });

      // 添加新标签
      this.visitedTabs.push({
        path,
        title,
        time: new Date().getTime()
      });
      
      // 如果标签数量超过10个，移除最早访问的标签
      if (this.visitedTabs.length > 10) {
        this.visitedTabs.sort((a, b) => a.time - b.time);
        const removed = this.visitedTabs.shift();
        console.log('移除最早的标签:', removed);
      }
      
      this.activeTab = path;
      this.saveTabs();
  },
    handleTabClick(tab) {
      // 点击标签时跳转到对应路由
      try {
        // tab是el-tab对象，而不是我们的visitedTabs中的对象
        // 在el-tabs组件中，tab.name就是我们绑定的路径值
        const currentPath = this.$route.path;
        const targetPath = tab.name;
        
        // 记录操作到控制台，便于调试
        console.log('标签点击事件触发:', { 
          tab: tab, 
          currentPath: currentPath, 
          targetPath: targetPath 
        });
        
        // 如果目标路径与当前路径相同，不进行导航
        if (currentPath === targetPath) {
          console.log('已经在当前页面，无需导航');
          return;
        }
        
        // 更新菜单激活状态 - 从路径中提取路由名称（不含前导斜杠）
        const routePath = targetPath.startsWith('/') ? targetPath.substring(1) : targetPath;
        window.sessionStorage.setItem('activePath', routePath);
        
        // 检查是否是特殊路由（带参数的路由）
        const isSpecialRoute = targetPath.includes('/storeedit/') || 
                              targetPath.includes('/storemark/') || 
                              targetPath.includes('/device/');
        
        if (isSpecialRoute) {
          // 对于特殊路由，使用完全刷新的方式导航
          console.log('特殊路由，使用完全刷新方式导航');
          window.location.href = '#' + targetPath;
        } else {
          // 正常导航
          console.log('执行路由导航到:', targetPath);
          this.$router.push(targetPath).catch(err => {
            // 忽略重复导航错误
            if (err.name !== 'NavigationDuplicated') {
              console.error('标签导航错误:', err);
            }
          });
        }
      } catch (error) {
        console.error('标签点击处理错误:', error);
        // 出错时尝试使用更直接的方式导航
        if (tab && tab.name) {
          window.location.href = '#' + tab.name;
        }
      }
    },
    handleTabRemove(targetPath) {
      // 关闭标签
      const tabs = this.visitedTabs
      let activeTab = this.activeTab
      
      // 在关闭标签页之前，标记全局变量表示正在关闭标签页
      window._isClosingTab = true;
      
      // 修改axios默认配置，标记关闭标签页的请求
      if (window.axios && window.axios.defaults) {
        window.axios.defaults.headers.common['X-Tab-Closing'] = 'true';
      }
      
      // 如果关闭的是当前激活的标签，需要激活下一个标签
      if (activeTab === targetPath) {
        tabs.forEach((tab, index) => {
          if (tab.path === targetPath) {
            const nextTab = tabs[index + 1] || tabs[index - 1]
            if (nextTab) {
              activeTab = nextTab.path
            } else {
              // 如果没有其他标签了，跳转到首页
              activeTab = '/index'
            }
          }
        })
      }
      
      this.activeTab = activeTab
      this.visitedTabs = tabs.filter(tab => tab.path !== targetPath)
      this.saveTabs()
      
      // 如果关闭的是当前标签，跳转到新的激活标签
      if (this.$route.path === targetPath) {
        // 使用自定义配置进行路由跳转
        this.$router.push({
          path: activeTab,
          query: { _closeTab: Date.now() } // 添加一个时间戳参数避免缓存
        })
      }
      
      // 设置一个定时器，在路由跳转完成后重置标记
      setTimeout(() => {
        window._isClosingTab = false;
        if (window.axios && window.axios.defaults) {
          delete window.axios.defaults.headers.common['X-Tab-Closing'];
        }
      }, 500);
    },
    // 静默关闭指定路径的标签（不触发路由跳转，导航由调用方负责）
    closeTabByPath(path) {
      if (!path) return;
      const index = this.visitedTabs.findIndex(tab => tab.path === path);
      if (index === -1) return;
      const wasActive = this.activeTab === path;
      this.visitedTabs.splice(index, 1);
      // 关闭的是当前激活标签时，顺延到相邻标签
      if (wasActive) {
        const nextTab = this.visitedTabs[index] || this.visitedTabs[index - 1];
        this.activeTab = nextTab ? nextTab.path : '/index';
      }
      this.saveTabs();
    },
    closeAllTabs() {
      // 关闭所有标签，保留首页
      this.visitedTabs = this.visitedTabs.filter(tab => tab.path === '/index' || tab.path === '/welcome')
      this.activeTab = '/index'
      this.saveTabs()
      this.$router.push('/index')
    },
    closeOtherTabs() {
      // 关闭其他标签，只保留当前标签和首页
      this.visitedTabs = this.visitedTabs.filter(tab => 
        tab.path === this.activeTab || tab.path === '/index' || tab.path === '/welcome'
      )
      this.saveTabs()
    },
    
    closeRightTabs() {
      // 关闭右侧标签
      const currentIndex = this.visitedTabs.findIndex(tab => tab.path === this.activeTab)
      if (currentIndex !== -1) {
        this.visitedTabs = this.visitedTabs.filter((tab, index) => 
          index <= currentIndex || tab.path === '/index' || tab.path === '/welcome'
        )
        this.saveTabs()
      }
    },
    
    handleTabsAction(command) {
      switch (command) {
        case 'closeRight':
          this.closeRightTabs()
          break
        case 'closeOthers':
          this.closeOtherTabs()
          break
        case 'closeAll':
          this.closeAllTabs()
          break
        default:
          break
      }
    }
  }
}
</script>

<style scoped>
.tabs-view-container {
  background-color: #fff;
  border-bottom: 1px solid #dcdfe6;
  margin-bottom: 10px;
}

.tabs-view-header {
  position: relative;
  display: flex;
  align-items: center;
}

/* 新增标签主区域样式 */
.tabs-main {
  display: flex;
  flex: 1;
  position: relative;
  align-items: center;
}

.tabs-main .el-tabs {
  flex: 1;
  overflow: hidden;
}

/* 标签操作按钮容器 */
.tabs-actions-wrapper {
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 10px;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 10;
  background-color: #fff;
}

.tabs-action-button {
  padding: 0;
  margin-left: 5px;
}

.tabs-action-button i {
  font-size: 16px;
  color: #909399;
}

.tabs-action-button:hover i {
  color: #409EFF;
}

/* 使用默认Element UI样式，只做小调整 */
/deep/ .el-tabs__header {
  margin: 0;
  padding: 5px 15px 0 0; /* 左侧不再内缩，第一个标签与下方内容区左缘对齐 */
}

/deep/ .el-tabs__nav-wrap {
  padding-right: 40px; /* 为操作按钮留出空间 */
}

/deep/ .el-tabs__nav-wrap::after {
  height: 1px;
}
</style>
